import { AlertTriangle, Loader2 } from 'lucide-react'

export function Card({ title, sub, children, className = '', right }) {
  return (
    <div className={`card p-4 mb-4 ${className}`}>
      {(title || right) && (
        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
          {title && <h3 className="text-[15px] font-semibold text-slate-800">{title}</h3>}
          {right}
        </div>
      )}
      {sub && <div className="text-[12.5px] text-slate-500 mb-3 leading-relaxed">{sub}</div>}
      {children}
    </div>
  )
}

export function ErrorBox({ error, what = 'data' }) {
  const d = error?.response?.data?.detail
  const msg = typeof d === 'string' ? d : (error?.message || 'Request failed')
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2.5 mb-4">
      Couldn&apos;t load {what}: {msg}
    </div>
  )
}

export function Loading({ label = 'Loading…' }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
      <Loader2 size={16} className="animate-spin" /> {label}
    </div>
  )
}

export function Empty({ children }) {
  return <div className="text-sm text-slate-500 py-6 text-center">{children}</div>
}

/** Amber banner listing missing_inputs (deduplicated). Renders nothing when empty. */
export function MissingInputs({ items }) {
  const list = [...new Set((items || []).filter(Boolean))]
  if (!list.length) return null
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-[12.5px] px-3 py-2.5 mb-4 flex gap-2">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-amber-500" />
      <div>
        <p className="font-semibold mb-0.5">Some figures are incomplete, so they show as “—”:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          {list.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </div>
    </div>
  )
}

const TAG = {
  portal:       ['Portal', 'bg-slate-100 text-slate-600'],
  xero:         ['Xero', 'bg-sky-100 text-sky-700'],
  pass_through: ['Pass-through', 'bg-amber-100 text-amber-800'],
}

export function SourceTag({ source }) {
  const t = TAG[source]
  if (!t) return null
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-px rounded-full ml-1.5 align-[1px] whitespace-nowrap ${t[1]}`}>
      {t[0]}
    </span>
  )
}

/** Segmented control. options: [[value, label]]. */
export function Seg({ options, value, onChange }) {
  return (
    <div className="inline-flex border border-slate-300 rounded-lg overflow-hidden bg-white">
      {options.map(([v, label], i) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={[
            'px-3 py-1.5 text-[13px]',
            i > 0 ? 'border-l border-slate-300' : '',
            value === v ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50',
          ].join(' ')}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

/** Thin progress bar, clamped to 0–100%. */
export function Progress({ ratio }) {
  const p = Number.isFinite(ratio) ? Math.max(0, Math.min(1, ratio)) : 0
  return (
    <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-1 mb-3">
      <div className="h-full bg-emerald-500" style={{ width: `${(p * 100).toFixed(0)}%` }} />
    </div>
  )
}
